import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import OrganizationForm from "../components/admin/OrganizationForm";
import OrganizationTable from "../components/admin/OrganizationTable";
import Modal from "react-bootstrap/Modal";
import { PageHeader } from "../components/layout/PageHeader";
import { confirmDialog } from "../components/common/ConfirmDialog";

const PORT = process.env.REACT_APP_PORT;

function Admin() {
  const [org, setOrg] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [form, setForm] = useState({
    id: null, name: "", address: "", type: "private",
    externalId: "", walletAddress: "", web3Id: "", emailId: "", status: "pending",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const fetchOrganizations = useCallback(async (q = "") => {
    try {
      const response = await axios.get(`${PORT}getOrganizations`, { params: q ? { q } : {} });
      setOrg([...response.data].sort((a, b) => a.id - b.id));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch organizations");
    }
  }, []);

  useEffect(() => { fetchOrganizations(searchQ); }, [fetchOrganizations, searchQ]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formToSubmit = { ...form };
      if (!isUpdating) formToSubmit.status = "pending";

      if (isUpdating) {
        await axios.put(`${PORT}updateOrganization/${currentOrgId}`, formToSubmit, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Organization updated");
      } else {
        await axios.post(`${PORT}createOrganization`, formToSubmit, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Organization added");
      }
      fetchOrganizations(searchQ);
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error(isUpdating ? "Failed to update organization" : "Failed to add organization");
    }
  };

  const handleDeleteClick = async (id) => {
    const target = org.find((o) => o.id === id);
    const ok = await confirmDialog({
      title: "Delete organization?",
      message: `This permanently removes ${target?.name || "this organization"}. This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${PORT}deleteOrganization/${id}`);
      fetchOrganizations(searchQ);
      toast.success("Organization deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete organization");
    }
  };

  const resetForm = () => {
    setForm({
      id: null, name: "", address: "", type: "private",
      externalId: "", walletAddress: "", web3Id: "", emailId: "", status: "pending",
    });
    setIsUpdating(false);
    setCurrentOrgId(null);
  };

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => { resetForm(); setModalIsOpen(false); };

  const handleUpdateClickWithModal = (id) => {
    const orgToEdit = org.find((o) => o.id === id);
    if (orgToEdit) {
      setForm(orgToEdit);
      setIsUpdating(true);
      setCurrentOrgId(id);
      openModal();
    }
  };

  const handleNewClick = () => { resetForm(); openModal(); };

  return (
    <div>
      <PageHeader pageName="Organization" />
      <div className="main-page-content fade-in">
        <OrganizationTable
          org={org}
          onSearchChange={setSearchQ}
          onNew={handleNewClick}
          handleUpdateClick={handleUpdateClickWithModal}
          handleDeleteClick={handleDeleteClick}
        />
      </div>

      {modalIsOpen && (
        <Modal show={modalIsOpen} onHide={closeModal} contentClassName="modal-content" centered>
          <Modal.Header closeButton={false}>
            <Modal.Title style={{ fontFamily: "var(--font-serif)" }}>
              {isUpdating ? "Edit organization" : "Add organization"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <OrganizationForm
              form={form}
              handleChange={handleChange}
              onCancel={closeModal}
              handleSubmit={(e) => { handleSubmit(e); closeModal(); }}
              isUpdating={isUpdating}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

export default Admin;
